import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import styled from "@emotion/styled";
import Cropper from "react-easy-crop";
import { setColor } from "../../../../../assets/styles/styles";
import PreviewImg from "../../../../../assets/images/mediaPreview/Web_Graphic_MyRoom_IntraportPreview.png";
import WebGraphicMyRoomPlaceholderSquare from "../../../../../assets/images/mediaPreview/Web_Graphic_MyRoom_Placeholder_Square.png";
import WebGraphicMyRoomPlaceholderVertical from "../../../../../assets/images/mediaPreview/Web_Graphic_MyRoom_Placeholder_Vertical.png";
import WebGraphicMyRoomPlaceholderVideo from "../../../../../assets/images/mediaPreview/Web_Graphic_MyRoom_Placeholder_Video.png";
import { ReactComponent as UploadIcon } from "../../../../../assets/images/mediaPreview/Vector.svg";
import { default as Button } from "../../../../../components/common/button/Button";
import { getCroppedImg } from "./getCroppedImg";

const UploadModal = ({ selected, setIsModalOpen, editSelectedFrame, onSubmitUploadedMedia }) => {
    console.log("UploadModal selected:", selected, editSelectedFrame);
    const [dragActive, setDragActive] = useState(false);
    const [file, setFile] = useState(null);
    const [imageSrc, setImageSrc] = useState(null); // for preview
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [boolFinalCrop, setBoolFinalCrop] = useState(false);
    const inputRef = useRef(null);

    const isVideo = selected === "videoFrame";
    const allowedTypes = isVideo ? ["video/mp4", "video/quicktime"] : ["image/png", "image/jpeg"];
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;

    const getAspect = () => {
        switch (selected) {
            case "squareImageFrame":
                return 1 / 1;

            case "verticalImageFrame1":
            case "verticalImageFrame2":
                return 9 / 16;

            case "intraportPreview":
                return 16 / 9;

            default:
                return 1 / 1;
        }
    };

    const aspect = getAspect();

    const cropperHeight =
        aspect >= 1
            ? 300 // wide → shorter height
            : 450; // tall → taller height

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === "dragenter" || e.type === "dragover") setDragActive(true);
        else if (e.type === "dragleave") setDragActive(false);
        setBoolFinalCrop(false);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            processFile(e.dataTransfer.files[0]);
        }
        setBoolFinalCrop(false);
    };

    const processFile = (uploaded) => {
        if (!allowedTypes.includes(uploaded.type)) {
            console.log(`Invalid file type. Allowed: ${allowedTypes.join(", ")}`);
            return;
        }
        if (uploaded.size > maxSize) {
            console.log(`File too large. Max size: ${maxSize / (1024 * 1024)}MB`);
            return;
        }
        setFile(uploaded);

        // For images, show preview and enable crop
        if (!isVideo) {
            const reader = new FileReader();
            reader.onload = () => setImageSrc(reader.result);
            reader.readAsDataURL(uploaded);
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            processFile(e.target.files[0]);
        }
        setBoolFinalCrop(false);
    };

    const handleSubmit = async () => {
        console.log("Submitting file:", file);
        if (!file) return;
        onSubmitUploadedMedia(editSelectedFrame.id, file);
    };

    /* ----------------------- Crop Handlers ----------------------- */
    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleCropSave = async () => {
        try {
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);

            const croppedFile = new File([croppedImageBlob], file.name, { type: file.type });

            setFile(croppedFile);

            // ✅ Update preview for UI
            const previewURL = URL.createObjectURL(croppedImageBlob);
            setImageSrc(previewURL);

            // This triggers the "Upload Image" button to show
            setBoolFinalCrop(true);
        } catch (e) {
            console.error(e);
        }
    };

    /* ----------------------- Frame Config ----------------------- */
    const getFrameConfig = () => {
        switch (selected) {
            case "intraportPreview":
                return {
                    title: "Upload Intraport Preview",
                    description: (
                        <>
                            Customize by uploading an image for your <Yellow>Intraport</Yellow>.
                        </>
                    ),
                    placeholderImg: PreviewImg,
                    text: "Intraport Preview",
                };
            case "squareImageFrame":
                return {
                    title: "Upload Image",
                    description: (
                        <>
                            Customize by uploading an image for <Yellow>Square Image Frame</Yellow>.
                        </>
                    ),
                    placeholderImg: WebGraphicMyRoomPlaceholderSquare,
                    text: "Square Image Frame",
                };
            case "verticalImageFrame1":
                return {
                    title: "Vertical Image Frame 1",
                    description: (
                        <>
                            Customize by uploading an image for <Yellow>Vertical Image Frame 1</Yellow>.
                        </>
                    ),
                    placeholderImg: WebGraphicMyRoomPlaceholderVertical,
                    text: "Vertical Image Frame 1",
                };
            case "verticalImageFrame2":
                return {
                    title: "Vertical Image Frame 2",
                    description: (
                        <>
                            Customize by uploading an image for <Yellow>Vertical Image Frame 2</Yellow>.
                        </>
                    ),
                    placeholderImg: WebGraphicMyRoomPlaceholderVertical,
                    text: "Vertical Image Frame 2",
                };
            case "videoFrame":
                return {
                    title: "Video Frame",
                    description: (
                        <>
                            Customize by uploading a Video for <Yellow>Video Frame</Yellow>.
                        </>
                    ),
                    placeholderImg: WebGraphicMyRoomPlaceholderVideo,
                    text: "Video Frame",
                };
            default:
                return {
                    title: "Upload Media",
                    description: (
                        <>
                            Select a media frame to upload your <Yellow>media</Yellow>.
                        </>
                    ),
                    placeholderImg: PreviewImg,
                    text: "Media Preview",
                };
        }
    };

    const { placeholderImg, title, description, text } = getFrameConfig();

    return (
        <>
            <ModalContent onDragEnter={handleDrag}>
                <Head>{title}</Head>
                <Description>{description}</Description>
                {selected !== "videoFrame" && (
                    <>
                        <PanelContent>
                            <Sides>
                                <CardImage src={placeholderImg} />
                                {text}
                            </Sides>

                            <Sides>
                                <CardImage src={editSelectedFrame?.img} />
                                Current Image
                            </Sides>
                        </PanelContent>
                        <Title>Add Image</Title>
                        <UploadContainer
                            dragActive={dragActive}
                            onDragEnter={handleDrag}
                            onDragLeave={handleDrag}
                            onDragOver={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => inputRef.current.click()}
                        >
                            <StyledUploadIcon />
                            <Description> Drag or browse a file to upload. </Description>
                            <input
                                type="file"
                                ref={inputRef}
                                style={{ display: "none" }}
                                onChange={handleFileChange}
                                accept={allowedTypes.join(",")}
                            />
                        </UploadContainer>
                        {file && <SelectedFileText>Selected: {file.name}</SelectedFileText>}
                    </>
                )}
                {selected === "videoFrame" && (
                    <>
                        <Title>Add Video</Title>
                        <UploadContainer>
                            <StyledUploadIcon />
                            <Description> Drag or browse a video file to upload. </Description>
                        </UploadContainer>
                    </>
                )}
                <ThirdPhase>
                    <Row>
                        <SuggestionTitle>File Type:</SuggestionTitle>
                        {selected === "videoFrame" && <SuggestionText>MP4 or MOV</SuggestionText>}
                        {selected !== "videoFrame" && <SuggestionText>PNG or JPG</SuggestionText>}
                    </Row>
                    <Row>
                        <SuggestionTitle>Recommended size: </SuggestionTitle>
                        {selected === "videoFrame" && <SuggestionText> 1920 x 1080 pixels</SuggestionText>}
                        {selected !== "videoFrame" && <SuggestionText> 1080 x 1920 pixels</SuggestionText>}
                    </Row>
                    <Row>
                        <SuggestionTitle>Maximum file size:</SuggestionTitle>
                        {selected === "videoFrame" && <SuggestionText> 50 MB</SuggestionText>}
                        {selected !== "videoFrame" && <SuggestionText>5 MB</SuggestionText>}
                    </Row>
                </ThirdPhase>

                {selected === "videoFrame" && (
                    <>
                        <Container>
                            <SpanTextMain>Rename</SpanTextMain>
                            <SubContainer>
                                <SpanText>Optional</SpanText>
                                <SpanTextMain> 0/50</SpanTextMain>
                            </SubContainer>
                        </Container>
                        <InputBox
                            type="text"
                            placeholder="Name this video"
                            // onClick={(e) => handleDropDowns(e, field)}
                            // id={field.key}
                            // value={getFieldValue(field)}
                            // onChange={(e) => handleFieldChange(e, field)}
                        />
                        <ThirdPhase videoFrame>
                            <Row>
                                <SuggestionTitle color>Add a short title for your video.</SuggestionTitle>
                            </Row>
                            <Row>
                                <SuggestionTitle color>Character Limit: </SuggestionTitle>
                                <SuggestionText color> 50 </SuggestionText>
                            </Row>
                        </ThirdPhase>
                    </>
                )}

                {!boolFinalCrop && !isVideo && file && (
                    <>
                        <div style={{ position: "relative", width: "100%", height: cropperHeight }}>
                            <Cropper
                                image={imageSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={aspect}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        </div>

                        <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between" }}>
                            <input
                                type="range"
                                min={1}
                                max={3}
                                step={0.1}
                                value={zoom}
                                onChange={(e) => setZoom(Number(e.target.value))}
                            />
                        </div>
                    </>
                )}

                {boolFinalCrop && imageSrc && !isVideo && (
                    <FifthPhase>
                        <img
                            src={imageSrc}
                            alt="Cropped Preview"
                            style={{
                                maxWidth: "100%",
                                maxHeight: "200px",
                                borderRadius: "8px",
                                objectFit: "cover",
                            }}
                        />
                        <div
                            style={{
                                marginTop: 10,
                                marginBottom: 10,
                                color: "#aaa",
                                fontSize: 14,
                                alignSelf: "center",
                            }}
                        >
                            Your cropped image preview
                        </div>
                    </FifthPhase>
                )}

                {!boolFinalCrop && file && !isVideo && (
                    <>
                        <Button
                            variant="PinkButton"
                            label="Crop Image"
                            onClick={handleCropSave}
                            buttonWidth={"100%"}
                            margin={"10px 0 10px 0px"}
                        />
                    </>
                )}

                {boolFinalCrop && (
                    <Button
                        variant={"PinkButton"}
                        disabled={!file}
                        label={selected === "videoFrame" ? "Upload Video" : "Upload Image"}
                        buttonWidth={"100%"}
                        onClick={handleSubmit}
                    />
                )}
                <GoBackText onClick={() => setIsModalOpen(true)}> Go Back </GoBackText>
            </ModalContent>
        </>
    );
};

export default UploadModal;

UploadModal.propTypes = {
    selected: PropTypes.string.isRequired,
    setIsModalOpen: PropTypes.func.isRequired,
    editSelectedFrame: PropTypes.shape({
        img: PropTypes.string,
    }),
};

const Container = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;
    margin-bottom: 5px;
`;

const InputBox = styled.input`
    padding: 10px;
    color: var(--White---Black-Light-Grey, #9d9d9d);
    margin: 0rem 0rem 0rem 0rem;
    width: 100%;
    border-radius: 5px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    background: var(--black-40, rgba(0, 0, 0, 0.4));
`;

const SubContainer = styled.div`
    display: flex;
    gap: 10px;
    align-items: center;
`;

const SpanTextMain = styled.span`
    font-size: 14px;
    font-weight: 700;
    color: var(--Text-Primary, #9d9d9d);
`;

const SpanText = styled.span`
    font-size: 14px;
    color: var(--Text-Secondary, #9d9d9d);
`;

const FifthPhase = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    margin: 20px 0;
    border-radius: 8px;
`;

const ModalWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    padding: 30px 40px;
    box-sizing: border-box;
`;

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 700px;
    color: ${setColor.inputText};
    border-radius: 10px;
    padding: 30px;
`;

const Head = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: center;
    font-family: Barlow;
    font-size: 24px;
    font-weight: 700;
    text-transform: capitalize;
`;

const Description = styled.div`
    text-align: center;
    margin-top: 15px;
    font-size: 14px;
`;

const Yellow = styled.span`
    color: #f5c518;
    font-weight: 700;
`;

const PanelContent = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-around;
    align-items: center;
    margin-top: 25px;
    padding: 20px;
    background: linear-gradient(
        90deg,
        rgba(157, 117, 245, 0) 0%,
        rgba(157, 117, 245, 0.1) 25%,
        rgba(157, 117, 245, 0.1) 75%,
        rgba(157, 117, 245, 0) 100%
    );
    border-radius: 10px;
    box-sizing: border-box;
`;

const ThirdPhase = styled.div`
    display: flex;
    flex-direction: column;
    gap: 5px;
    width: 100%;
    padding: 15px 20px;
    margin: 20px 0;
    border-radius: 8px;
    background: ${(props) =>
        props.videoFrame
            ? "rgba(255, 255, 255, 0.05)"
            : "linear-gradient(360deg, rgba(157, 117, 245, 0) 0%, rgba(157, 117, 245, 0.1) 25%, rgba(157, 117, 245, 0.1) 83%, rgba(157, 117, 245, 0.1) 100%)"};
`;

const Sides = styled.div`
    width: 45%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
`;

const CardImage = styled.img`
    width: 100%;
    max-width: 250px;
    height: 150px;
    border-radius: 5px;
    margin-bottom: 10px;
    border: 1px solid rgba(255, 255, 255, 0.1);
    object-fit: cover;
`;

const Title = styled.div`
    font-size: 14px;
    font-weight: 600;
    margin: 15px 0 5px;
    align-self: flex-start;
`;

const UploadContainer = styled.div`
    display: flex;
    height: 120px;
    width: 100%;
    justify-content: center;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    border: 2px dashed rgba(255, 255, 255, 0.2);
    background: rgba(0, 0, 0, 0.65);
`;

const Row = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const SuggestionTitle = styled.div`
    color: ${(props) => (props.color ? "var(--White---Black-Light-Grey, #9d9d9d)" : "#9d9d9d")};
    font-weight: 700;
    font-size: 13px;
`;

const SuggestionText = styled.div`
    color: ${(props) => (props.color ? "var(--White---Black-Light-Grey, #9d9d9d)" : "#9d9d9d")};
    margin-left: 5px;
    font-size: 13px;
`;

const GoBackText = styled.div`
    color: var(--Text-Primary, #fbfcff);
    text-align: center;
    font-family: Barlow;
    font-size: 16px;
    font-weight: 600;
    margin-top: 20px;
    cursor: pointer;
`;

const StyledUploadIcon = styled(UploadIcon)`
    width: 30px;
    height: 30px;
`;

const SelectedFileText = styled.div`
    margin-top: 10px;
    color: #aaa;
    font-size: 14px;
`;
